import { LOCATION_CHANGE, routerMiddleware } from 'connected-react-router'
import { History } from 'history'
import { enableMapSet } from 'immer'
import { setGlobalDevModeChecks } from 'reselect'

// Reselect v5 throws in dev mode when result functions return values
// derived directly from input (identity function detection). Multiple
// existing selectors trigger this (e.g., safeDecodeJwt returning undefined
// for undefined input), causing white-screen crashes during SSR and
// client hydration. Disable globally until selectors are refactored.
setGlobalDevModeChecks({ identityFunctionCheck: 'never' })
import _ from 'lodash'
import {
  AnyAction,
  applyMiddleware,
  compose,
  createStore,
  DeepPartial,
  MiddlewareAPI,
  Store,
} from 'redux'
import thunk, { ThunkAction, ThunkDispatch, ThunkMiddleware } from 'redux-thunk'
import { EnvConfig, Environments } from 'src/configurations'
import { cspSigninUrl, resetPasswordUrl, signinUrl } from 'src/navigation/urls'
import createRootReducer, {
  initialRootState,
  LocationState,
  RootState,
} from 'src/redux/createRootReducer'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'

enableMapSet()

export type ThunkDis = ThunkDispatch<RootState, void, AnyAction>
export type ThunkStore = Store<RootState, AnyAction> & { dispatch: ThunkDis }
export type ThunkActionWithProperties = ThunkAction<
  unknown,
  RootState,
  undefined,
  AnyAction
> & { isDispatchedFromBackgroundPage?: boolean }

const functionPropertyPreservingThunk: ThunkMiddleware =
  (store) =>
  (next) =>
  (action): ReturnType<ThunkMiddleware> => {
    const functionPropertyPreservingDispatch: ThunkDispatch<
      RootState,
      undefined,
      AnyAction
    > = (act: AnyAction | ThunkActionWithProperties) => {
      act.isDispatchedFromBackgroundPage = action.isDispatchedFromBackgroundPage
      return store.dispatch(act)
    }
    return thunk({ ...store, dispatch: functionPropertyPreservingDispatch })(
      next,
    )(action)
  }

const enhancedLocationChangeMiddleware =
  (store: MiddlewareAPI<ThunkDis, RootState>) =>
  (next: ThunkDis) =>
  (action: AnyAction): unknown => {
    if (action.type === LOCATION_CHANGE) {
      const state = store.getState()
      const currentBackgroundLocationPathname =
        selectCalculatedBackgroundLocation(state).pathname
      const nextBackgroundLocationPathname =
        action.payload.location.state?.backgroundLocation?.pathname
      return next({
        ...action,
        payload: {
          ...action.payload,
          shouldResetBackgroundState:
            currentBackgroundLocationPathname !==
              nextBackgroundLocationPathname &&
            // hard coding these checks in here so that the eamil state is not interfered with
            currentBackgroundLocationPathname !== resetPasswordUrl() &&
            currentBackgroundLocationPathname !== cspSigninUrl() &&
            currentBackgroundLocationPathname !== signinUrl(),
          isFirstRendering: false, // TODO: this seems like a weird feature from connected-react-router... figure this out
        },
      })
    } else {
      return next(action)
    }
  }

const actionLocationPathnameMiddleware =
  (store: MiddlewareAPI<ThunkDis, RootState>) =>
  (next: ThunkDis) =>
  (action: AnyAction): unknown => {
    const state = store.getState()
    const location = selectLocationFromState(state)
    const calculatedBackgroundLocation =
      selectCalculatedBackgroundLocation(state)
    return next({
      ...action,
      location,
      calculatedBackgroundLocation,
    })
  }

const configureStore = (
  initialState: DeepPartial<RootState>,
  history: History<LocationState>,
  envConfig: EnvConfig,
  isClient: boolean,
): ThunkStore => {
  const composeEnhancers =
    envConfig.ENVIRONMENT !== Environments.Prod &&
    isClient &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
          trace: true,
          traceLimit: 25,
        })
      : compose

  const store = createStore(
    createRootReducer(history),
    _.merge({}, initialRootState, initialState),
    composeEnhancers(
      applyMiddleware<ThunkDis, RootState>(
        routerMiddleware(history),
        functionPropertyPreservingThunk,
        enhancedLocationChangeMiddleware,
        actionLocationPathnameMiddleware,
      ),
    ),
  )

  return store
}

export default configureStore
